// Input validation and sanitization utilities

// Email validation
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Roll number validation (adjust pattern based on your institution)
export const isValidRollNo = (rollNo: string): boolean => {
  // Example pattern: alphanumeric, 6-12 characters
  const rollNoRegex = /^[A-Za-z0-9]{6,12}$/;
  return rollNoRegex.test(rollNo);
};

// Password strength validation
export const isStrongPassword = (password: string): boolean => {
  // At least 8 characters, one uppercase, one lowercase, one number
  const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
  return strongPasswordRegex.test(password);
};

// Name validation (letters, spaces, hyphens, apostrophes)
export const isValidName = (name: string): boolean => {
  const nameRegex = /^[a-zA-Z\s\-']{2,50}$/;
  return nameRegex.test(name);
};

// Department validation
export const isValidDepartment = (dept: string): boolean => {
  const validDepartments = [
    'Computer Science',
    'Electrical Engineering',
    'Mechanical Engineering',
    'Civil Engineering',
    'Business Administration',
    'Arts & Sciences',
    'Other'
  ];
  return validDepartments.includes(dept);
};

// Sanitize HTML to prevent XSS
export const sanitizeHTML = (dirty: string): string => {
  // Basic HTML entity encoding
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  const reg = /[&<>"'/]/ig;
  return dirty.replace(reg, (match) => map[match]);
};

// Sanitize user input for display
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  // Remove any script tags or dangerous HTML
  let clean = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  clean = clean.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
  clean = clean.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
  clean = clean.replace(/javascript:/gi, '');
  
  // Encode HTML entities
  return sanitizeHTML(clean);
};

// URL validation
export const isValidURL = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// File type validation
export const isValidImageType = (fileType: string): boolean => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  return validTypes.includes(fileType.toLowerCase());
};

// File size validation (in bytes)
export const isValidFileSize = (size: number, maxSizeMB: number = 5): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return size > 0 && size <= maxSizeBytes;
};

// Comment/text length validation
export const isValidTextLength = (text: string, minLength: number = 1, maxLength: number = 1000): boolean => {
  const trimmedLength = text.trim().length;
  return trimmedLength >= minLength && trimmedLength <= maxLength;
};

// Location validation
export const isValidLocation = (location: string): boolean => {
  // Allow alphanumeric, spaces, commas, periods, hyphens
  const locationRegex = /^[a-zA-Z0-9\s,.\-]{3,100}$/;
  return locationRegex.test(location);
};

// Points validation
export const isValidPoints = (points: number): boolean => {
  return Number.isInteger(points) && points >= 0 && points <= 1000000;
};

// Validation error messages
export const ValidationErrors = {
  EMAIL: 'Please enter a valid email address',
  ROLL_NO: 'Roll number must be 6-12 alphanumeric characters',
  PASSWORD: 'Password must be at least 8 characters with uppercase, lowercase, and numbers',
  PASSWORD_MATCH: 'Passwords do not match',
  NAME: 'Name must be 2-50 characters and contain only letters, spaces, hyphens, or apostrophes',
  DEPARTMENT: 'Please select a valid department',
  FILE_TYPE: 'Only JPEG, PNG, WebP, and GIF images are allowed',
  FILE_SIZE: 'File size must not exceed 5MB',
  TEXT_LENGTH: 'Text length is invalid',
  LOCATION: 'Location must be 3-100 characters',
  REQUIRED: 'This field is required',
  GENERIC: 'Invalid input',
};

// Form field validator
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: any) => boolean;
  message?: string;
}

export const validateField = (value: any, rules: ValidationRule[]): string | null => {
  for (const rule of rules) {
    if (rule.required && !value) {
      return rule.message || ValidationErrors.REQUIRED;
    }
    
    if (value && rule.minLength && value.length < rule.minLength) {
      return rule.message || `Minimum length is ${rule.minLength} characters`;
    }
    
    if (value && rule.maxLength && value.length > rule.maxLength) {
      return rule.message || `Maximum length is ${rule.maxLength} characters`;
    }
    
    if (value && rule.pattern && !rule.pattern.test(value)) {
      return rule.message || ValidationErrors.GENERIC;
    }
    
    if (value && rule.custom && !rule.custom(value)) {
      return rule.message || ValidationErrors.GENERIC;
    }
  }
  
  return null;
};

// Batch validation
export interface FieldValidation {
  field: string;
  value: any;
  rules: ValidationRule[];
}

export const validateFields = (validations: FieldValidation[]): { [field: string]: string } => {
  const errors: { [field: string]: string } = {};
  
  validations.forEach(({ field, value, rules }) => {
    const error = validateField(value, rules);
    if (error) {
      errors[field] = error;
    }
  });
  
  return errors;
};

// Sanitize object properties
export const sanitizeObject = <T extends Record<string, any>>(obj: T): T => {
  const sanitized = {} as T;
  
  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === 'string') {
      sanitized[key as keyof T] = sanitizeInput(value) as T[keyof T];
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key as keyof T] = sanitizeObject(value);
    } else {
      sanitized[key as keyof T] = value;
    }
  });
  
  return sanitized;
};

// CSRF token validation (for forms)
export const generateCSRFToken = (): string => {
  return Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

export const validateCSRFToken = (token: string, storedToken: string): boolean => {
  return token === storedToken && token.length === 64;
};